import {
  readPortalAccess,
  type PortalAccess,
} from "@/services/jambushrustiAuth";
// Deliberately separate from the library portal's `libraryAdminSession` key.
const ADMIN_SESSION_KEY = "cmsAdminSession";
let refreshRequest: Promise<string> | null = null;

export interface AdminApiUser {
  id: string;
  email: string;
  name: string;
  role: string;
  avatar?: string;
}

export interface AdminApiSession extends PortalAccess {
  accessToken: string;
  refreshToken?: string;
  user: AdminApiUser;
}

type ApiPayload = Record<string, any>;

const getStorage = (remember: boolean) =>
  remember ? window.localStorage : window.sessionStorage;

export function getAdminSession(): AdminApiSession | null {
  for (const storage of [window.localStorage, window.sessionStorage]) {
    try {
      const value = storage.getItem(ADMIN_SESSION_KEY);
      if (value) {
        const session = JSON.parse(value) as AdminApiSession;
        if (session.can_access_cms === true && session.accessToken) {
          return session;
        }
        storage.removeItem(ADMIN_SESSION_KEY);
      }
    } catch {
      storage.removeItem(ADMIN_SESSION_KEY);
    }
  }
  return null;
}

export async function loginAdmin(
  email: string,
  password: string,
  remember = false
): Promise<AdminApiSession> {
  const baseUrl = import.meta.env.DEV
    ? "/jambushrusti-api"
    : (
        import.meta.env.VITE_API_BASE_URL ||
        "https://api.jambushusti.universal-software.in/api"
      ).replace(/\/$/, "");
  const tenantId =
    import.meta.env.VITE_X_TENANTID || "6cf833d7-bf6f-4688-9136-5674d8053b85";

  const response = await fetch(`${baseUrl}/jambushrusti/auth/login/`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Tenant-ID": tenantId,
    },
    body: JSON.stringify({ email, password }),
  });
  const payload = (await response.json().catch(() => ({}))) as ApiPayload;

  if (!response.ok) {
    const message =
      payload.detail ||
      payload.message ||
      payload.error?.message ||
      payload.error;
    throw new Error(
      typeof message === "string" ? message : "Invalid email or password."
    );
  }

  const data = payload.data || payload;
  const tokens = data.tokens || data.token || data;
  const accessToken =
    tokens.access || tokens.access_token || data.accessToken || data.token;
  const refreshToken =
    tokens.refresh || tokens.refresh_token || data.refreshToken;
  const apiUser = data.user || payload.user || {};

  if (!accessToken || typeof accessToken !== "string") {
    throw new Error("The login response did not include an access token.");
  }

  const access = readPortalAccess(payload);
  if (!access.can_access_cms) {
    throw new Error(
      "This account does not have CMS access. Please sign in to the Library portal if you have Library access."
    );
  }
  const role = "admin";
  const session: AdminApiSession = {
    ...access,
    accessToken,
    refreshToken,
    user: {
      id: String(apiUser.id || apiUser.uuid || apiUser.user_id || email),
      email: apiUser.email || email,
      name:
        apiUser.name ||
        apiUser.full_name ||
        [apiUser.first_name, apiUser.last_name].filter(Boolean).join(" ") ||
        email.split("@")[0],
      role,
      avatar: apiUser.avatar || apiUser.avatar_url,
    },
  };

  localStorage.removeItem(ADMIN_SESSION_KEY);
  sessionStorage.removeItem(ADMIN_SESSION_KEY);
  getStorage(remember).setItem(ADMIN_SESSION_KEY, JSON.stringify(session));
  return session;
}

export async function refreshAdminToken(): Promise<string> {
  if (refreshRequest) {
    return refreshRequest;
  }

  refreshRequest = (async () => {
    const baseUrl = import.meta.env.DEV
      ? "/jambushrusti-api"
      : (
          import.meta.env.VITE_API_BASE_URL ||
          "https://api.jambushusti.universal-software.in/api"
        ).replace(/\/$/, "");
    const tenantId =
      import.meta.env.VITE_X_TENANTID || "6cf833d7-bf6f-4688-9136-5674d8053b85";
    const session = getAdminSession();

    if (!baseUrl || !tenantId || !session?.refreshToken) {
      logoutAdmin();
      throw new Error("Your session has expired. Please sign in again.");
    }

    const response = await fetch(`${baseUrl}/auth/token-refresh/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Tenant-Id": tenantId,
      },
      body: JSON.stringify({ refresh: session.refreshToken }),
    });

    const payload = (await response.json().catch(() => ({}))) as ApiPayload;
    const data = payload.data || payload;
    const tokens = data.tokens || data.token || data;
    const accessToken =
      tokens.access || tokens.access_token || data.accessToken || data.token;

    if (!response.ok || !accessToken) {
      logoutAdmin();
      const message =
        payload.detail ||
        payload.message ||
        payload.error?.message ||
        payload.error;
      throw new Error(
        typeof message === "string"
          ? message
          : "Your session has expired. Please sign in again."
      );
    }

    const updatedSession: AdminApiSession = {
      ...session,
      accessToken,
      refreshToken:
        tokens.refresh ||
        tokens.refresh_token ||
        data.refreshToken ||
        session.refreshToken,
    };

    // Check where it was stored to update the correct one
    if (localStorage.getItem(ADMIN_SESSION_KEY)) {
      localStorage.setItem(ADMIN_SESSION_KEY, JSON.stringify(updatedSession));
    } else if (sessionStorage.getItem(ADMIN_SESSION_KEY)) {
      sessionStorage.setItem(ADMIN_SESSION_KEY, JSON.stringify(updatedSession));
    }

    return accessToken;
  })();

  try {
    return await refreshRequest;
  } finally {
    refreshRequest = null;
  }
}

export async function logoutAdminFromServer(): Promise<void> {
  const baseUrl = import.meta.env.DEV
    ? "/jambushrusti-api"
    : (
        import.meta.env.VITE_API_BASE_URL ||
        "https://api.jambushusti.universal-software.in/api"
      ).replace(/\/$/, "");
  const tenantId =
    import.meta.env.VITE_X_TENANTID || "6cf833d7-bf6f-4688-9136-5674d8053b85";
  const session = getAdminSession();

  try {
    if (baseUrl && tenantId && session?.accessToken) {
      await fetch(`${baseUrl}/auth/logout/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.accessToken}`,
          "X-Tenant-ID": tenantId,
        },
        body: JSON.stringify({ refresh: session.refreshToken || "" }),
      });
    }
  } catch (error) {
    // Ignore server logout errors
  } finally {
    logoutAdmin();
  }
}

export function logoutAdmin(): void {
  localStorage.removeItem(ADMIN_SESSION_KEY);
  sessionStorage.removeItem(ADMIN_SESSION_KEY);
}

export async function cmsFetch(
  url: string | URL | Request,
  options: RequestInit = {}
): Promise<Response> {
  const session = getAdminSession();
  const tenantId =
    import.meta.env.VITE_X_TENANTID || "6cf833d7-bf6f-4688-9136-5674d8053b85";

  const getHeaders = (token: string | undefined) => {
    const headers = new Headers(options.headers);
    if (!headers.has("Content-Type") && !(options.body instanceof FormData)) {
      headers.set("Content-Type", "application/json");
    }
    headers.set("X-Tenant-ID", tenantId);
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }
    return headers;
  };

  let response = await fetch(url, {
    ...options,
    headers: getHeaders(session?.accessToken),
  });

  if (response.status === 401 && session?.refreshToken) {
    try {
      const newToken = await refreshAdminToken();
      response = await fetch(url, {
        ...options,
        headers: getHeaders(newToken),
      });
    } catch (e) {
      // Refresh failed, token is invalid/expired
    }
  }

  return response;
}
